import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLastLogged } from '../../hooks/useLastLogged';

// ── Helpers ───────────────────────────────────────────────────────────────────

function ageInMonths(dateOfBirth) {
  if (!dateOfBirth) return 0;
  const dob = new Date(dateOfBirth);
  const now = new Date();
  return (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
}

function formatRelative(date, nowMs) {
  const diffMs = nowMs - date.getTime();
  if (diffMs < 60000) return 'just now';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m ago` : `${h}h ago`;
}

// Staleness thresholds per collection (hours). Sleep has none — patterns vary too much.
const THRESHOLDS = {
  bottleFeeds:   { warnH: 3, dangerH: 4 },
  breastfeeding: { warnH: 3, dangerH: 4 },
  diapers:       { warnH: 3, dangerH: 4 },
  solidFoods:    { warnH: 5, dangerH: 7 },
  waterIntake:   { warnH: 5, dangerH: 7 },
};

function getStaleness(name, date, nowMs) {
  if (!date) return null;
  const t = THRESHOLDS[name];
  if (!t) return null;
  const hours = (nowMs - date.getTime()) / 3600000;
  if (hours >= t.dangerH) return 'danger';
  if (hours >= t.warnH)   return 'warning';
  return null;
}

// ── Section config ─────────────────────────────────────────────────────────────
// always     — shown regardless of age or history
// minMonths  — shown when child is this age or older
// recentDays — shown only if something was logged within this many days

const ALL_SECTIONS = [
  { name: 'bottleFeeds',       label: 'Bottle',       icon: '🍼', to: '/app/bottle-feeds',        always:     true },
  { name: 'breastfeeding',     label: 'Breast',       icon: '🤱', to: '/app/breast-feeding',      always:     true },
  { name: 'diapers',           label: 'Nappies',      icon: '🧷', to: '/app/diapers',             always:     true },
  { name: 'sleepTracking',     label: 'Sleep',        icon: '😴', to: '/app/sleep',               always:     true },
  { name: 'solidFoods',        label: 'Solids',       icon: '🥣', to: '/app/solid-foods',         minMonths:  6    },
  { name: 'waterIntake',       label: 'Water',        icon: '💧', to: '/app/water-intake',        minMonths:  6    },
  { name: 'foodIntroductions', label: 'Food Intros',  icon: '🍽️', to: '/app/food-introductions', recentDays: 7    },
  { name: 'growthHealth',      label: 'Growth',       icon: '📏', to: '/app/growth-health',       recentDays: 7    },
  { name: 'milestones',        label: 'Milestones',   icon: '⭐', to: '/app/milestones',          recentDays: 7    },
  { name: 'doctorVisits',      label: 'Doctor',       icon: '🏥', to: '/app/doctor-visits',       recentDays: 7    },
  { name: 'vaccinations',      label: 'Vaccines',     icon: '💉', to: '/app/vaccinations',        recentDays: 7    },
  { name: 'allergies',         label: 'Allergies',    icon: '⚠️', to: '/app/allergies',           recentDays: 7    },
  { name: 'healthRecords',     label: 'Health',       icon: '📋', to: '/app/health-records',      recentDays: 7    },
];

const ALL_NAMES = ALL_SECTIONS.map(s => s.name);

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * LastLoggedStrip — a compact 2-column grid near the top of the dashboard
 * showing how long ago each relevant section was last logged.
 *
 * Ticks every 60 seconds so the displayed times stay accurate while the app
 * is open. Colour-codes overdue sections using the same warning/danger scheme
 * used elsewhere in the app.
 */
export default function LastLoggedStrip({ childId, dateOfBirth }) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Tick every minute to keep relative times accurate
  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  const lastDates  = useLastLogged(childId, ALL_NAMES);
  const ageMonths  = useMemo(() => ageInMonths(dateOfBirth), [dateOfBirth]);

  const visible = useMemo(() => ALL_SECTIONS.filter(s => {
    if (s.always)    return true;
    if (s.minMonths  && ageMonths >= s.minMonths) return true;
    if (s.recentDays) {
      const last = lastDates[s.name];
      return last && (nowMs - last.getTime()) / 86400000 <= s.recentDays;
    }
    return false;
  }), [lastDates, ageMonths, nowMs]);

  if (!childId) return null;

  return (
    <div className="last-logged-strip mb-3">
      <p className="last-logged-header">Last logged</p>
      <div className="last-logged-grid">
        {visible.map(section => {
          const last      = lastDates[section.name];
          const staleness = getStaleness(section.name, last, nowMs);
          const timeStr   = last ? formatRelative(last, nowMs) : 'Never logged';

          return (
            <Link
              key={section.name}
              to={section.to}
              className={`last-logged-cell text-decoration-none${staleness ? ` last-logged-${staleness}` : ''}`}
            >
              <span className="last-logged-icon">{section.icon}</span>
              <span className="last-logged-label">{section.label}</span>
              <span className="last-logged-time">{timeStr}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
